import React, { useState, useEffect, useRef } from 'react';
import {
  Save, Loader2, CheckCircle, ShieldCheck,
  Link2, Copy, Check, XCircle, Palette, Calendar, Wifi, SquareParking, Accessibility, Video,
} from 'lucide-react';
import { auth } from '../config/firebase';
import { usuarioService, configuracaoService } from '../services/api';
import PaywallCard from '../components/PaywallCard';

const FRONTEND_URL = 'https://www.agendasincro.com.br';
const CORES_PADRAO = { corPrimaria: '#1883FF', corSecundaria: '#6D28D9', corFundo: '#F8F7FF' };

const DIFERENCIAIS_DISPONIVEIS = [
  { chave: 'wifi', label: 'Wi-Fi', icone: Wifi },
  { chave: 'estacionamento', label: 'Estacionamento', icone: SquareParking },
  { chave: 'acessibilidade', label: 'Acessibilidade', icone: Accessibility },
  { chave: 'atendimento_online', label: 'Atendimento Online', icone: Video },
];

// nome/telefone/endereco/diferenciais/formas_pagamento não são editáveis aqui (isso é da aba
// "Dados do Estabelecimento"), mas precisam ser carregados e reenviados junto no salvar — o
// endpoint de perfil espera o objeto completo, e a pré-visualização também usa nome/diferenciais.
const PaginaPublicaTab = ({ temAcesso }) => {
  const isPremium = temAcesso('diferenciais_cores');
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [endereco, setEndereco] = useState('');
  const [diferenciais, setDiferenciais] = useState([]);
  const [formasPagamento, setFormasPagamento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const [slug, setSlug] = useState('');
  const [slugOriginal, setSlugOriginal] = useState('');
  const [slugStatus, setSlugStatus] = useState('idle'); // idle | checking | disponivel | indisponivel
  const [linkCopiado, setLinkCopiado] = useState(false);
  const debounceRef = useRef(null);

  const [tema, setTema] = useState(CORES_PADRAO);

  useEffect(() => {
    const carregarDados = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const [resPerfil, resTema] = await Promise.all([
          usuarioService.obterPerfil(user.uid),
          configuracaoService.obterTema(user.uid),
        ]);
        setNome(resPerfil.data.nome || '');
        setTelefone(resPerfil.data.telefone || '');
        setEndereco(resPerfil.data.endereco || '');
        setDiferenciais(resPerfil.data.diferenciais || []);
        setFormasPagamento(resPerfil.data.formas_pagamento || null);
        setSlug(resPerfil.data.slug || '');
        setSlugOriginal(resPerfil.data.slug || '');
        setTema({ ...CORES_PADRAO, ...resTema.data });
      } catch (error) {
        console.error("Erro ao carregar perfil:", error);
      } finally {
        setLoading(false);
      }
    };
    carregarDados();
  }, []);

  const handleSlugChange = (valor) => {
    const slugFormatado = valor.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
    setSlug(slugFormatado);
    setLinkCopiado(false);

    clearTimeout(debounceRef.current);

    if (!slugFormatado || slugFormatado === slugOriginal) {
      setSlugStatus('idle');
      return;
    }

    setSlugStatus('checking');
    debounceRef.current = setTimeout(async () => {
      try {
        const uid = auth.currentUser.uid;
        const res = await usuarioService.verificarSlugDisponivel(slugFormatado, uid);
        setSlugStatus(res.data.disponivel ? 'disponivel' : 'indisponivel');
      } catch (error) {
        setSlugStatus('idle');
      }
    }, 500);
  };

  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(`${FRONTEND_URL}/${slug}`);
      setLinkCopiado(true);
      setTimeout(() => setLinkCopiado(false), 2500);
    } catch (error) {
      console.error("Erro ao copiar link:", error);
    }
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    if (slugStatus === 'indisponivel') {
      return alert("Esse link já está em uso. Escolha outro antes de salvar.");
    }

    setSaving(true);
    setSuccess(false);

    try {
      const uid = auth.currentUser.uid;
      await Promise.all([
        usuarioService.atualizarPerfil(uid, { nome, telefone, slug, endereco, diferenciais, formas_pagamento: formasPagamento }),
        configuracaoService.salvarTema(uid, tema),
      ]);
      setSlugOriginal(slug);
      setSlugStatus('idle');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
      alert(error.response?.data?.erro || "Erro ao salvar as alterações do perfil.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-14 text-center text-slate-500">
        <Loader2 className="animate-spin text-[#1883FF] mb-4" size={40} />
        <p className="text-[10px] font-black uppercase tracking-[0.2em]">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[900px] animate-fade-in-up">
      <form onSubmit={handleSalvar} className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">

        <div className="flex flex-col gap-6">

          {/* LINK PÚBLICO */}
          <div className="bg-white rounded-[28px] border border-slate-100 shadow-sm p-6">
            <h3 className="text-xs font-black uppercase text-[#1e293b] tracking-wide mb-5 flex items-center gap-1.5">
              <Link2 size={14} className="text-[#6D28D9]" /> Seu Link de Agendamento
            </h3>

            <div className="flex items-center gap-2 bg-[#F8F7FF] border-2 border-transparent rounded-2xl px-4 py-1 transition-all duration-300 focus-within:bg-white focus-within:border-[#6D28D9]/30 focus-within:ring-4 focus-within:ring-[#6D28D9]/10">
              <span className="text-xs font-bold text-slate-400 whitespace-nowrap">agendasincro.com.br/</span>
              <input
                type="text"
                className="w-full py-3.5 border-none bg-transparent outline-none text-sm font-bold text-[#1e293b] placeholder-slate-300"
                value={slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                placeholder="seu-negocio"
              />
              {slugStatus === 'checking' && <Loader2 size={16} className="animate-spin text-slate-400 shrink-0" />}
              {slugStatus === 'disponivel' && <Check size={16} className="text-emerald-500 shrink-0" />}
              {slugStatus === 'indisponivel' && <XCircle size={16} className="text-red-500 shrink-0" />}
            </div>

            {slugStatus === 'indisponivel' && (
              <p className="text-[10px] text-red-500 font-bold ml-1 mt-2">Esse link já está em uso. Escolha outro.</p>
            )}

            {slug && (
              <button
                type="button"
                onClick={copiarLink}
                className="mt-3 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide text-[#6D28D9] bg-[#F5F3FF] border-none px-3 py-2 rounded-lg cursor-pointer hover:bg-[#EDE9FE] transition-colors"
              >
                {linkCopiado ? <><Check size={12} /> Copiado!</> : <><Copy size={12} /> Copiar link</>}
              </button>
            )}

            <p className="text-[10px] text-slate-400 font-semibold mt-3">
              Compartilhe esse link com seus clientes para eles agendarem direto, sem precisar do WhatsApp.
            </p>
          </div>

          {/* CORES DA PÁGINA PÚBLICA */}
          {isPremium ? (
            <div className="bg-white rounded-[28px] border border-slate-100 shadow-sm p-6">
              <h3 className="text-xs font-black uppercase text-[#1e293b] tracking-wide mb-5 flex items-center gap-1.5">
                <Palette size={14} className="text-[#6D28D9]" /> Cores da Página Pública
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {[
                  { chave: 'corPrimaria', label: 'Cor Primária' },
                  { chave: 'corSecundaria', label: 'Cor Secundária' },
                  { chave: 'corFundo', label: 'Cor de Fundo' },
                ].map(({ chave, label }) => (
                  <div key={chave} className="flex flex-col gap-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">{label}</label>
                    <div className="flex items-center gap-2 bg-[#F8F7FF] border-2 border-transparent rounded-2xl px-3 py-2 focus-within:border-[#6D28D9]/30">
                      <input
                        type="color"
                        value={tema[chave]}
                        onChange={e => setTema({ ...tema, [chave]: e.target.value })}
                        className="w-9 h-9 rounded-lg border-none cursor-pointer bg-transparent"
                      />
                      <input
                        type="text"
                        value={tema[chave]}
                        onChange={e => setTema({ ...tema, [chave]: e.target.value })}
                        className="w-full bg-transparent border-none outline-none text-xs font-bold text-slate-700 uppercase"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setTema(CORES_PADRAO)}
                className="mt-5 text-[10px] font-black uppercase tracking-wide text-[#6D28D9] bg-[#F5F3FF] border-none px-3 py-2 rounded-lg cursor-pointer hover:bg-[#EDE9FE] transition-colors"
              >
                Restaurar cores padrão
              </button>
            </div>
          ) : (
            <PaywallCard
              titulo="Cores da Página Pública"
              descricao="Personalize as cores do seu link de agendamento."
              plano="Comum"
              className="min-h-0"
            />
          )}

          <button
            type="submit"
            disabled={saving}
            className={`w-full py-4 border-none rounded-2xl text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2.5 transition-all duration-400 cursor-pointer shadow-lg active:scale-98 disabled:opacity-60 disabled:cursor-not-allowed ${success ? 'bg-[#059669] shadow-emerald-600/20' : 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9] shadow-[#6D28D9]/20 hover:opacity-90 hover:-translate-y-0.5'}`}
          >
            {saving ? <Loader2 className="animate-spin" size={20} /> : success ? <CheckCircle size={20} /> : <Save size={20} />}
            {saving ? 'Salvando...' : success ? 'Salvo!' : 'Salvar Alterações'}
          </button>

          <div className="p-4 bg-[#f0fdf4] rounded-2xl border border-[#dcfce7] flex items-center gap-3 text-[#166534] text-xs font-semibold leading-relaxed">
            <ShieldCheck size={20} className="text-emerald-600 shrink-0" />
            <p className="m-0">Seus dados estão protegidos pela criptografia de ponta a ponta da <strong>Agenda Sincro</strong>.</p>
          </div>
        </div>

        {/* PRÉ-VISUALIZAÇÃO */}
        <div className="flex flex-col gap-3">
          <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Pré-visualização</p>
          <div
            className="rounded-[28px] border border-slate-100 shadow-sm p-6 flex flex-col gap-4"
            style={{ background: `linear-gradient(to bottom right, ${tema.corFundo}, #ffffff)` }}
          >
            <div className="text-center">
              <p className="text-[9px] font-black uppercase tracking-[0.2em] mb-1" style={{ color: tema.corSecundaria }}>Agenda Sincro</p>
              <p className="text-base font-black uppercase italic text-slate-900 m-0">{nome || 'Seu Estabelecimento'}</p>
              {endereco && <p className="text-[10px] font-semibold text-slate-400 mt-1">{endereco}</p>}
              {diferenciais.length > 0 && (
                <div className="flex items-center justify-center gap-2 mt-2">
                  {DIFERENCIAIS_DISPONIVEIS.filter(d => diferenciais.includes(d.chave)).map(({ chave, icone: Icone }) => (
                    <Icone key={chave} size={14} style={{ color: tema.corSecundaria }} />
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 p-4 flex flex-col gap-3 shadow-sm">
              <div className="flex items-center gap-2 text-xs font-bold text-slate-600">
                <Calendar size={14} style={{ color: tema.corPrimaria }} /> Escolha um horário
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {['09:00', '10:00', '11:00'].map((h, i) => (
                  <div
                    key={h}
                    className="py-2 rounded-lg text-[10px] font-black text-center"
                    style={i === 1
                      ? { background: `linear-gradient(to right, ${tema.corPrimaria}, ${tema.corSecundaria})`, color: '#fff' }
                      : { background: tema.corFundo, color: '#475569' }}
                  >
                    {h}
                  </div>
                ))}
              </div>
              <div
                className="w-full py-3 rounded-xl text-[10px] font-black uppercase tracking-wider text-white text-center"
                style={{ background: `linear-gradient(to right, ${tema.corPrimaria}, ${tema.corSecundaria})` }}
              >
                Confirmar Agendamento
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PaginaPublicaTab;
